import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { FC, useCallback, useEffect, useState } from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useSendVehicleReminderSmsMutation } from '../../redux/api/vehicleApi'
import { VehicleReminder, VehicleReminderMessageInfoTypeEnum } from '../../sdk'
import { defaultReminderMessage } from '../../utils/defaultReminderMessage/defaultReminderMessage'
import { describeCustomer } from '../../utils/describeCustomer/describeCustomer'
import { PhoneNumberSelect } from '../PhoneNumberSelect'
import { DelayedSendPicker } from '../SharedUI/DelayedSendPicker'
import { dateOnly, getEarliestReminderDate } from './VehicleReminderList'

interface Props {
  vehicleReminder: VehicleReminder
  open: boolean
  handleClose: () => void
  handleSent?: (id: string) => void
}

export const SendReminderDialog: FC<Props> = ({
  vehicleReminder,
  open,
  handleClose,
  handleSent,
  ...props
}) => {
  const { accessToken } = useAccessTokenAndShopId()
  const [sendVehicleReminderSms] = useSendVehicleReminderSmsMutation()

  const earlierInspectionDate =
    vehicleReminder.vehicleEntry?.info?.nextInspectionDate &&
    getEarliestReminderDate(vehicleReminder)!.toISOString() ===
      dateOnly(
        vehicleReminder.vehicleEntry?.info?.nextInspectionDate
      )!.toISOString()

  const [reminder, setReminder] = useState<VehicleReminderMessageInfoTypeEnum>(
    earlierInspectionDate
      ? VehicleReminderMessageInfoTypeEnum.Inspection
      : VehicleReminderMessageInfoTypeEnum.OilService
  )

  const [customer, setCustomer] = useState<string>(
    vehicleReminder.customerEntries![0]!.id!
  )

  const isMarketingOptOut = vehicleReminder.customerEntries!.find(
    (vr) => vr.id === customer
  )!.information?.marketingOptOut

  const [smsNumber, setSmsNumber] = useState<string | undefined>('')

  const customerEntry = vehicleReminder.customerEntries!.find(
    (vr) => vr.id === customer
  )!

  const [messageText, setMessageText] = useState<string>(
    defaultReminderMessage(
      customerEntry,
      vehicleReminder.vehicleEntry,
      reminder
    )
  )

  useEffect(() => {
    if (reminder) {
      setMessageText(
        defaultReminderMessage(
          customerEntry,
          vehicleReminder.vehicleEntry,
          reminder
        )
      )
    }
  }, [customerEntry, reminder, vehicleReminder.vehicleEntry])

  const [useDelay, setUseDelay] = useState<boolean>(false)
  const minDateTime = dayjs().add(15, 'minutes')
  const maxDateTime = dayjs().add(1, 'week')
  const [delayedDateTime, setDelayedDateTime] = useState<Dayjs>(
    minDateTime.add(1, 'minutes')
  )
  const [delayedError, setDelayedError] = useState<boolean>(false)

  const handleSend = useCallback(async () => {
    if (delayedError) {
      return
    }

    const vehicleReminderMessageId = await sendVehicleReminderSms({
      authorization: accessToken,
      sendVehicleReminderSmsRequest: {
        info: {
          vehicle: vehicleReminder.vehicleEntry!.id!,
          type: reminder,
          message: messageText
        },
        customer: customerEntry!.id!,
        phoneNumber: smsNumber!,
        sendAt: useDelay ? delayedDateTime.toDate() : undefined
      }
    })

    if (handleSent && 'data' in vehicleReminderMessageId) {
      handleSent(vehicleReminder.vehicleEntry!.id!)
    }

    handleClose()
  }, [
    accessToken,
    customerEntry,
    delayedDateTime,
    delayedError,
    handleClose,
    handleSent,
    messageText,
    reminder,
    sendVehicleReminderSms,
    smsNumber,
    useDelay,
    vehicleReminder.vehicleEntry
  ])

  return (
    <Dialog {...props} open={open} onClose={() => handleClose()}>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Send Reminder</DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <Typography pb={2}>
          Select customer, phone number, and type your message to send a
          reminder
        </Typography>

        <FormControl>
          <FormLabel>Reminder Type</FormLabel>
          <RadioGroup
            value={reminder}
            onChange={(event) => {
              setReminder(
                event.target.value as VehicleReminderMessageInfoTypeEnum
              )
            }}>
            <FormControlLabel
              value={VehicleReminderMessageInfoTypeEnum.Inspection}
              control={<Radio />}
              label="Next Inspection Date"
            />
            <FormControlLabel
              value={VehicleReminderMessageInfoTypeEnum.OilService}
              control={<Radio />}
              label="Next Oil Service Date"
            />
          </RadioGroup>
        </FormControl>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth error={isMarketingOptOut}>
            <InputLabel id="selectCustomer">Select Customer</InputLabel>
            <Select
              label="Select Customer"
              value={customer}
              onChange={(event) => {
                setCustomer(event.target.value)
              }}
              fullWidth>
              {vehicleReminder.customerEntries!.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {describeCustomer(option)}
                </MenuItem>
              ))}
            </Select>
            {isMarketingOptOut && (
              <FormHelperText>
                This customer has opted out of marketing. Do you still want to
                send this SMS?
              </FormHelperText>
            )}
          </FormControl>

          {customer && (
            <PhoneNumberSelect
              customerId={customer}
              number={smsNumber}
              handleNumberChange={(number) => setSmsNumber(number)}
            />
          )}

          {customer && smsNumber && (
            <>
              <Box>
                <Typography pl={1}>SMS Message</Typography>

                <TextField
                  fullWidth
                  multiline
                  placeholder="Type your message here..."
                  type="text"
                  onChange={(event) => setMessageText(event.target.value)}
                  value={messageText}
                />
              </Box>

              <DelayedSendPicker
                useDelay={useDelay}
                setUseDelay={setUseDelay}
                delayedDateTime={delayedDateTime}
                setDelayedDateTime={setDelayedDateTime}
                setError={setDelayedError}
                minDateTime={minDateTime}
                maxDateTime={maxDateTime}
                label="Schedule Timing"
                helperText="Time must be between 15 minutes and 1 week"
              />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ pt: 2 }}>
        <Button variant="text" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="text"
          onClick={handleSend}
          disabled={!customer || !smsNumber}>
          Send SMS
        </Button>
      </DialogActions>
    </Dialog>
  )
}
